import apiClient from './apiClient';

/**
 * Geographic & ISP Service (Shared)
 *
 * Handles geographic and ISP data lookups for DNS routing.
 * These APIs are read-only and shared between user and admin.
 */
class GeoService {
  // ==========================================
  // Geographic Information
  // ==========================================

  /**
   * Get all continents
   */
  async getContinents() {
    try {
      const response = await apiClient.get('/api/user/dns/geo/continents');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get countries by continent
   * @param {number} continentId - Continent ID
   */
  async getCountriesByContinent(continentId) {
    try {
      const response = await apiClient.get(`/api/user/dns/geo/countries?continent_id=${continentId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get regions by country
   * @param {string} countryId - Country code
   */
  async getRegions(countryId) {
    try {
      const response = await apiClient.get(`/api/user/dns/geo/regions?country_code=${countryId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get cities by country and region
   * @param {string} countryId - Country code
   * @param {number} regionId - Region ID
   */
  async getCities(countryId, regionId) {
    try {
      const response = await apiClient.get(`/api/user/dns/geo/cities?country_code=${countryId}&region_id=${regionId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get continent information by IDs
   * @param {array} continentIds - Array of continent IDs
   */
  async getContinentInfo(continentIds) {
    try {
      const response = await apiClient.get(`/api/user/dns/geo/info/continents?ids=${continentIds.join(',')}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get country information by IDs
   * @param {array} countryIds - Array of country codes
   */
  async getCountryInfo(countryIds) {
    try {
      const response = await apiClient.get(`/api/user/dns/geo/info/countries?ids=${countryIds.join(',')}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get province information by IDs
   * @param {array} provinceIds - Array of province IDs
   */
  async getProvinceInfo(provinceIds) {
    try {
      const response = await apiClient.get(`/api/user/dns/geo/info/provinces?ids=${provinceIds.join(',')}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get city information by IDs
   * @param {array} cityIds - Array of city IDs
   */
  async getCityInfo(cityIds) {
    try {
      const response = await apiClient.get(`/api/user/dns/geo/info/cities?ids=${cityIds.join(',')}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // ==========================================
  // ISP Information
  // ==========================================

  /**
   * Get countries with ISP data
   */
  async getIspCountries() {
    try {
      const response = await apiClient.get('/api/user/dns/isp/countries');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get ISP types for a country
   * @param {string} country - Country code
   */
  async getIspTypes(country) {
    try {
      const response = await apiClient.get(`/api/user/dns/isp/types?country=${encodeURIComponent(country)}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get ISP providers
   * @param {string} country - Country code
   * @param {string} type - Optional ISP type
   * @param {string} search - Optional search term
   */
  async getIsps(country, type = null, search = null) {
    let url = `/api/user/dns/isp/providers?country=${encodeURIComponent(country)}`;

    if (type) {
      url += `&usage_type=${encodeURIComponent(type)}`;
    }

    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }

    try {
      const response = await apiClient.get(url);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get ISP information by IDs
   * @param {array} ispIds - Array of ISP IDs
   */
  async getIspInfo(ispIds) {
    try {
      const response = await apiClient.get(`/api/user/dns/isp/info?ids=${ispIds.join(',')}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // ==========================================
  // CIDR Statistics (for CidrSelector)
  // ==========================================

  /**
   * Get CIDR statistics
   */
  async getCidrStats() {
    try {
      const response = await apiClient.get('/api/user/dns/cidr_stats');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }
}

export default new GeoService();
